import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider, Skeleton, TextInput } from '@mantine/core';
import { CornerUpLeft, CornerUpRight, Loader } from 'tabler-icons-react';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import { usePaginationFilter } from '../../../helpers/hooks';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { currencyFormat, singularPluralFormat } from '../../../helpers/format';
import AppImage from '../../common/AppImage';

const AdminStockContentStoreProductsList = ({ pkEcomStore }) => {
  const hasFetched = useRef(false);
  const { state, fetchPaginatedStoreProducts } = useContext(StoreContext);
  const {
    pageIndex,
    totalCount,
    totalPages,
    data,
    filter,
    loading: dataLoading,
    onPageChange,
    onFilterChange,
    onRefresh
  } = usePaginationFilter(
    {
      search: '',
      status: ''
    },
    (newFilter) => {
      fetchPaginatedStoreProducts(
        pkEcomStore,
        {
          ...newFilter,
          pageSize: 10
        },
        null,
        (error) => {
          triggerNotification(error);
        }
      );
    },
    state.paginatedEcomStoreProducts
  );
  const hasAlreadyFetched =
    state.paginatedEcomStoreProducts.filter?.fkEcomStore === pkEcomStore;
  const loading = !!pkEcomStore && (dataLoading || !hasAlreadyFetched);
  const productData = hasAlreadyFetched ? data : [];

  useEffect(() => {
    if (pkEcomStore && !hasAlreadyFetched) {
      onRefresh();
      hasFetched.current = true;
    }
  }, [pkEcomStore]);

  return !pkEcomStore ? (
    <AppStack style={{ padding: 16 }}>
      <AppText
        style={{
          fontSize: 14,
          color: '#666',
          textAlign: 'center',
          fontStyle: 'italic'
        }}
      >
        Please select a store to view its products.
      </AppText>
    </AppStack>
  ) : (
    <AppStack style={{ gap: 0 }}>
      <AppFlexbox
        style={{
          padding: 5,
          gap: 5,
          alignItems: 'end',
          justifyContent: 'space-between'
        }}
      >
        <TextInput
          onChange={(e) => {
            onFilterChange({ ...filter, search: e.currentTarget.value });
          }}
          placeholder="Search by name..."
          value={filter.search || ''}
        />

        <AppStack style={{ gap: 0, alignItems: 'flex-end' }}>
          {loading ? (
            <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
              <Loader color="grey" size={15} />
              <AppText style={{ color: 'grey', fontSize: 14 }}>results</AppText>
            </AppFlexbox>
          ) : (
            <AppText
              style={{ color: 'grey', fontSize: 14, textWrap: 'balance' }}
            >
              Showing {data.length} of{' '}
              {singularPluralFormat(totalCount, 'result', 'results')}
            </AppText>
          )}

          <AppFlexbox style={{ gap: 8 }}>
            <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
              {loading ? (
                <Loader color="grey" size={18} />
              ) : (
                <AppText style={{ fontSize: 14, color: '#666' }}>
                  ({pageIndex}/{totalPages})
                </AppText>
              )}

              <Button.Group>
                <Button
                  disabled={pageIndex <= 1 || loading}
                  onClick={() => onPageChange(pageIndex - 1)}
                  size="compact-md"
                  variant="default"
                >
                  <CornerUpLeft size={18} />
                </Button>
                <Button
                  disabled={pageIndex >= totalPages || loading}
                  onClick={() => onPageChange(pageIndex + 1)}
                  size="compact-md"
                  variant="default"
                >
                  <CornerUpRight size={18} />
                </Button>
              </Button.Group>
            </AppFlexbox>
          </AppFlexbox>
        </AppStack>
      </AppFlexbox>

      <Divider />

      <AppStack style={{ gap: 0 }}>
        {loading ? (
          Array.from(Array(25)).map((x, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              {index > 0 && <Divider />}

              <AppFlexbox
                style={{
                  padding: 8,
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                  <AppStack style={{ height: 50, width: 50 }}>
                    <Skeleton height={50} width={50} />
                  </AppStack>

                  <AppStack style={{ gap: 5 }}>
                    <Skeleton height={14} width={150} />
                    <Skeleton height={14} width={100} />
                  </AppStack>
                </AppFlexbox>
                <AppStack style={{ gap: 0, alignItems: 'flex-end' }}>
                  <Skeleton height={14} width={80} />
                </AppStack>
              </AppFlexbox>
            </React.Fragment>
          ))
        ) : productData.length === 0 ? (
          <AppStack style={{ padding: 16 }}>
            <AppText
              style={{
                fontSize: 14,
                color: '#666',
                textAlign: 'center',
                fontStyle: 'italic'
              }}
            >
              No products found for this store.
            </AppText>
          </AppStack>
        ) : (
          productData.map((p, index) => (
            <React.Fragment key={p.pkEcomStoreProduct}>
              {index > 0 && <Divider />}

              <AppFlexbox
                style={{
                  padding: 8,
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                  <AppStack style={{ height: 50, width: 50 }}>
                    <AppImage
                      height={50}
                      src={p.ecomVendorProduct.previewImage}
                      width={50}
                    />
                  </AppStack>

                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      {p.ecomVendorProduct.name}
                    </AppText>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      {singularPluralFormat(
                        p.ecomStoreProductVariantCount,
                        'variant',
                        'variants'
                      )}
                    </AppText>
                  </AppStack>
                </AppFlexbox>
                <AppStack style={{ gap: 0, alignItems: 'flex-end' }}>
                  <AppText style={{ fontSize: 14 }}>
                    {p.minWebstorePrice === p.maxWebstorePrice
                      ? currencyFormat(p.minWebstorePrice / 100)
                      : `${currencyFormat(
                          p.minWebstorePrice / 100
                        )} - ${currencyFormat(p.maxWebstorePrice / 100)}`}
                  </AppText>
                </AppStack>
              </AppFlexbox>
            </React.Fragment>
          ))
        )}
      </AppStack>
    </AppStack>
  );
};

AdminStockContentStoreProductsList.propTypes = {
  pkEcomStore: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default AdminStockContentStoreProductsList;
